/**
 * AgentUiActionsCard — sorties cliquables proposées par l'agent en fin de tour.
 *
 * **Pourquoi ce composant est si simple, comparé à `LaunchAnalysisCard`.** Les
 * cartes de consentement répondent à un agent SUSPENDU : elles doivent attendre
 * un verdict backend faisant autorité (`_result`) avant d'afficher le succès,
 * gérer un timeout de 5 min, un refus, un abort, et se souvenir de leur état —
 * d'où leur machine à états. Ici l'agent a **fini** : le clic ne fait que piloter
 * une navigation locale, idempotente, dans la surface qui monte le chat. Il n'y a
 * donc rien à corréler avec le backend. La carte attend néanmoins la navigation
 * locale afin de bloquer les doubles clics et de rétablir les boutons en erreur.
 *
 * Les identifiants d'action viennent d'un catalogue FERMÉ côté serveur
 * (`AGENT_UI_ACTION_IDS`) : le LLM choisit quand proposer et avec quel libellé,
 * jamais vers quoi pointer.
 *
 * @see docs/features/renumber-pieces/plans/2026-07-17-numerotation-agentique.html §07
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorService } from '@/lib/errors/ErrorService';
import type { AgentUiActionId, ResolvedAgentUiAction } from '@hector/shared-types';

interface AgentUiActionsCardProps {
  actions: ResolvedAgentUiAction[];
  /**
   * Fourni par l'hôte du chat. Absent = le chat est monté sur une surface qui ne
   * sait router aucune action (ex. `/assistant`) → on ne rend rien plutôt que des
   * boutons morts.
   */
  onAction?: (action: ResolvedAgentUiAction) => Promise<void>;
}

export function AgentUiActionsCard({ actions, onAction }: AgentUiActionsCardProps) {
  const [pendingActionId, setPendingActionId] = useState<AgentUiActionId | null>(null);

  if (!onAction || actions.length === 0) return null;

  const handleAction = async (action: ResolvedAgentUiAction): Promise<void> => {
    if (pendingActionId) return;
    setPendingActionId(action.id);
    try {
      await onAction(action);
    } catch (error) {
      ErrorService.handle(new Error(
        `Agent UI action execution failed: actionId=${action.id}. Keep the action card available and let the user retry.`,
        { cause: error },
      ), {
        userMessage: 'Impossible d’ouvrir cette action. Réessayez.',
        context: { action: 'execute_agent_ui_action' },
      });
    } finally {
      setPendingActionId(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 py-1">
      {actions.map((action, index) => (
        <Button
          key={action.id}
          type="button"
          size="sm"
          // La première action est la suite naturelle du flux (« Passer au
          // tamponnage ») ; les autres sont des détours (« Voir la numérotation »).
          variant={index === 0 ? 'default' : 'outline'}
          onClick={() => { void handleAction(action); }}
          disabled={pendingActionId !== null}
          aria-busy={pendingActionId === action.id}
          className="h-8 text-xs"
        >
          {pendingActionId === action.id && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {action.label}
        </Button>
      ))}
    </div>
  );
}
